import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Input, Select, Segmented, Tag } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { chargerReferentiels , optionsCouleursParFamille } from '../../components/ndoe';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import AcomptePanel from '../reservations/AcomptePanel';
import usePermissions from '../../hooks/usePermissions';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeImage, NdoeLoading, NdoeError,
} from '../../components/ndoe';
import './precommandes.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Formatage de date sans dayjs (proscrit — gel WebView). date_cr arrive en ISO
// ou 'YYYY-MM-DD HH:mm:ss' ; on garde la partie date en JJ/MM/AAAA.
const fmtDate = (v) => {
  if (!v) return '—';
  const d = String(v).slice(0, 10);
  const [y, m, j] = d.split('-');
  return j ? `${j}/${m}/${y}` : d;
};
const money = (v) => (v === null || v === undefined || Number(v) === 0 ? null : `${Number(v).toLocaleString('fr-FR')} $`);
const modeleTxt = (r) => {
  const base = r.nom_produit || r.note || 'Modèle non catalogué';
  const bouts = [base];
  if (r.marque) bouts.push(r.marque);
  const suff = [];
  if (r.taille) suff.push(`P${r.taille}`);
  if (r.couleur) suff.push(r.couleur);
  return `${bouts.join(' ')}${suff.length ? ` · ${suff.join(' · ')}` : ''}`;
};

// Badge de disponibilité LIVE d'une option en attente : la marchandise est-elle
// là maintenant ? (disponible_maintenant renvoyé par l'API, calculé en direct)
const dispoBadge = (r) => {
  if (r.statut !== 'EN_ATTENTE') return null;
  const d = Number(r.disponible_maintenant) || 0;
  if (d >= Number(r.qte || 1)) return <NdoeStatusBadge kind="custom" tone="ok" label="Arrivé · honorable" />;
  if (d > 0) return <NdoeStatusBadge kind="custom" tone="warn" label={`${d} dispo (< demande)`} />;
  return <NdoeStatusBadge kind="custom" tone="neutral" label="En attente" />;
};
const statutBadge = (r) => {
  if (r.statut === 'HONOREE') return <NdoeStatusBadge kind="custom" tone="gold" label="Honorée" />;
  if (r.statut === 'ANNULEE') return <NdoeStatusBadge kind="custom" tone="neutral" label="Annulée" />;
  if (r.statut === 'EXPIREE') return <NdoeStatusBadge kind="custom" tone="neutral" label="Expirée" />;
  return <NdoeStatusBadge kind="custom" tone="info" label="En attente" />;
};

export default function Precommandes() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const user = useSelector((s) => s.user?.currentUser);
  const allowed = ['admin', 'secretaire'].includes(String(user?.role || '').toLowerCase());

  const [vue, setVue] = useState('options');       // options | demande
  const [statut, setStatut] = useState('EN_ATTENTE');
  const [q, setQ] = useState('');
  const [data, setData] = useState([]);
  const [demande, setDemande] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (vue === 'demande') {
        const { data: d } = await axios.get(`${DOMAIN}/api/precommandes/demande`, { params: { q } });
        setDemande(d || []);
      } else {
        const { data: d } = await axios.get(`${DOMAIN}/api/precommandes`, { params: { statut, q } });
        setData(d || []);
      }
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les options.');
    } finally { setLoading(false); }
  }, [vue, statut, q]);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  // --- Création ------------------------------------------------------------
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [tailles, setTailles] = useState([]);
  const [couleurs, setCouleurs] = useState([]);
  const [refLists, setRefLists] = useState({ categories: [], marques: [], couleurs: [] });
  const emptyForm = { id_client: null, modele: null, id_taille: null, id_couleur: null, qte: 1, note: '' };

  // Les pointures, rangées et désambiguïsées. Un libellé porté par plusieurs
  // systèmes se voit suffixé de son système ; les autres restent nus.
  const optionsPointures = useMemo(() => {
    const parLibelle = {};
    (tailles || []).forEach((t) => { parLibelle[t.taille] = (parLibelle[t.taille] || 0) + 1; });
    return (tailles || [])
      .slice()
      .sort((a, b) => (Number(b.variantes || 0) - Number(a.variantes || 0))
        || (Number(a.taille) - Number(b.taille)))
      .map((t) => ({
        value: t.id_taille,
        label: parLibelle[t.taille] > 1 && t.code_pays
          ? `P${t.taille} (${t.code_pays})`
          : `P${t.taille}`,
      }));
  }, [tailles]);
  const [form, setForm] = useState(emptyForm);

  // UNE PANNE N'EST PAS UNE LISTE VIDE (10/08/2026).
  //
  // Les quatre référentiels étaient chargés avec des `catch` muets : `() => {}`
  // et un `console.log`. Une panne réseau donnait donc un formulaire aux menus
  // déroulants vides, sans le moindre message — et une précommande sans cliente
  // ni pointure ne peut pas être enregistrée.
  const [erreurRef, setErreurRef] = useState(null);
  const [essaiRef, setEssaiRef] = useState(0);

  useEffect(() => {
    if (!createOpen || clients.length) return;
    let vivant = true;
    (async () => {
      const { listes, erreur } = await chargerReferentiels([
        { cle: 'clients', quoi: 'les clientes', promesse: axios.get(`${DOMAIN}/api/client`) },
        { cle: 'tailles', quoi: 'les pointures', promesse: axios.get(`${DOMAIN}/api/produit/taille`) },
        { cle: 'categories', quoi: 'les catégories', promesse: axios.get(`${DOMAIN}/api/produit/categorie`) },
        { cle: 'marques', quoi: 'les marques', promesse: axios.get(`${DOMAIN}/api/produit/marque`) },
        { cle: 'couleurs', quoi: 'les couleurs', promesse: axios.get(`${DOMAIN}/api/produit/couleur`) },
      ]);
      if (!vivant) return;

      setErreurRef(erreur);
      setClients(listes.clients);
      setTailles(listes.tailles);
      // SAISIE : la couleur est enregistrée. Repliée par famille, sans compte
      // de nuances — ici on choisit, on ne filtre pas.
      const couleursOpt = optionsCouleursParFamille(listes.couleurs);
      setCouleurs(couleursOpt);
      setRefLists({
        categories: listes.categories.map((x) => ({ value: x.id_categorie, label: x.nom_categorie })),
        marques: listes.marques.map((x) => ({ value: x.id_marque, label: (x.nom || '').trim() })),
        couleurs: couleursOpt,
      });
    })();
    return () => { vivant = false; };
  }, [createOpen, clients.length, essaiRef]);

  // Sélection d'un modèle dans le catalogue : on retient l'identité du modèle,
  // et on PRÉ-remplit la pointure demandée avec celle de la variante repérée
  // (l'utilisateur peut la changer vers une pointure absente).
  const onPickModele = (v) => {
    setForm((f) => ({
      ...f,
      modele: { id_produit: v.id_produit, nom_produit: v.nom_produit, img: v.img, marque: v.marque },
      id_taille: f.id_taille || v.id_taille || null,
    }));
    setPickerOpen(false);
  };

  const submitCreate = async () => {
    if (!form.id_client) return toast.error('Sélectionnez la cliente.');
    if (!form.modele && !form.note.trim()) return toast.error('Choisissez un modèle du catalogue, ou décrivez le modèle souhaité en note.');
    if (form.modele && !form.id_taille) return toast.error('Précisez la pointure souhaitée.');
    setBusy(true);
    try {
      await axios.post(`${DOMAIN}/api/precommandes`, {
        id_client: form.id_client,
        id_produit: form.modele?.id_produit || null,
        id_taille: form.id_taille || null,
        id_couleur: form.id_couleur || null,
        qte: Number(form.qte) || 1,
        note: form.note,
      });
      toast.success('Option enregistrée : vous serez notifié à la prochaine réception correspondante.');
      setCreateOpen(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Création impossible.');
    } finally { setBusy(false); }
  };

  // --- Honorer / Annuler ----------------------------------------------------
  const [honor, setHonor] = useState(null);     // { pc, commentaire }
  const [cancel, setCancel] = useState(null);   // { pc, motif }
  const [acompte, setAcompte] = useState(null); // { type:'precommande', id } — cible du panneau

  const doHonor = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${DOMAIN}/api/precommandes/${honor.pc.id_precommande}/honorer`, {
        commentaire: honor.commentaire,
      });
      toast.success('Option honorée : la paire est réservée pour la cliente.');
      setHonor(null); load();
      if (d.id_reservation) navigate('/reservations');
    } catch (e) { toast.error(e.response?.data?.error || 'Impossible d’honorer maintenant.'); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await axios.put(`${DOMAIN}/api/precommandes/${cancel.pc.id_precommande}/annuler`, { motif: cancel.motif });
      toast.success('Option annulée.');
      setCancel(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Annulation impossible.'); }
    finally { setBusy(false); }
  };

  // --- Rendus ---------------------------------------------------------------
  const actionsFor = (r) => {
    if (r.statut !== 'EN_ATTENTE') {
      if (r.statut === 'HONOREE') {
        return [<NdoeActionButton key="v" size="sm" variant="secondary" onClick={() => navigate('/reservations')}>Voir la réservation</NdoeActionButton>];
      }
      return [];
    }
    const honorable = (Number(r.disponible_maintenant) || 0) >= Number(r.qte || 1);
    const list = [
      <NdoeActionButton key="h" size="sm" variant={honorable ? 'primary' : 'secondary'} onClick={() => setHonor({ pc: r, commentaire: '' })}>Honorer</NdoeActionButton>,
    ];
    // Acompte : encaisser sur une option porte sur du stock ABSENT — c'est le
    // sens même de la Phase 2. Visible pour qui a reservations.acompte.
    if (can('reservations.acompte')) {
      list.push(<NdoeActionButton key="ac" size="sm" variant="secondary" onClick={() => setAcompte({ type: 'precommande', id: r.id_precommande })}>Acompte</NdoeActionButton>);
    }
    list.push(<NdoeActionButton key="a" size="sm" variant="danger" onClick={() => setCancel({ pc: r, motif: '' })}>Annuler</NdoeActionButton>);
    return list;
  };

  const optionCols = [
    { title: 'Photo', dataIndex: 'img', key: 'img', render: (t, r) => <NdoeImage src={r.img} alt={modeleTxt(r)} size="lg" /> },
    { title: 'Modèle', key: 'modele', render: (t, r) => <b>{modeleTxt(r)}</b> },
    { title: 'Cliente', dataIndex: 'nom_client', key: 'cli', render: (t) => t || '—' },
    { title: 'Qté', dataIndex: 'qte', key: 'qte' },
    { title: 'Demandée le', key: 'date', render: (t, r) => fmtDate(r.date_cr) },
    { title: 'Disponible', key: 'dispo', render: (t, r) => dispoBadge(r) },
    { title: 'Acompte', key: 'ac', render: (t, r) => money(r.acompte_solde ?? r.acompte) || <span style={{ color: 'var(--ndoe-muted)' }}>—</span> },
    { title: 'Statut', key: 'st', render: (t, r) => statutBadge(r) },
    { title: 'Actions', key: 'act', render: (t, r) => <div className="pc-actions">{actionsFor(r)}</div> },
  ];
  const renderOptionCard = (r) => (
    <NdoeDataCard
      media={<NdoeImage src={r.img} alt={modeleTxt(r)} size="lg" />}
      title={modeleTxt(r)}
      subtitle={`${r.nom_client || '—'} · demandée le ${fmtDate(r.date_cr)}`}
      badge={r.statut === 'EN_ATTENTE' ? dispoBadge(r) : statutBadge(r)}
      metrics={[
        { value: r.qte, label: 'qté' },
        ...(money(r.acompte_solde ?? r.acompte) ? [{ value: money(r.acompte_solde ?? r.acompte), label: 'acompte' }] : []),
      ]}
      actions={actionsFor(r)}
    />
  );

  const demandeCols = [
    { title: 'Photo', dataIndex: 'img', key: 'img', render: (t, r) => <NdoeImage src={r.img} alt={modeleTxt(r)} size="lg" /> },
    { title: 'Modèle demandé', key: 'modele', render: (t, r) => <b>{modeleTxt(r)}</b> },
    { title: 'Clientes', dataIndex: 'nb_clientes', key: 'nc' },
    { title: 'Paires demandées', dataIndex: 'paires_demandees', key: 'pd', render: (t) => <b>{t}</b> },
    { title: 'Dont acompte', dataIndex: 'nb_avec_acompte', key: 'na', render: (t) => (Number(t) ? <Tag color="gold">{t}</Tag> : '—') },
    { title: 'Depuis', key: 'depuis', render: (t, r) => fmtDate(r.depuis) },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', render: (t) => t || '—' },
  ];
  const renderDemandeCard = (r) => (
    <NdoeDataCard
      media={<NdoeImage src={r.img} alt={modeleTxt(r)} size="lg" />}
      title={modeleTxt(r)}
      subtitle={`Depuis ${fmtDate(r.depuis)}`}
      metrics={[
        { value: r.paires_demandees, label: 'paires' },
        { value: r.nb_clientes, label: 'clientes' },
        ...(Number(r.nb_avec_acompte) ? [{ value: r.nb_avec_acompte, label: 'avec acompte' }] : []),
      ]}
    />
  );

  const enAttenteCount = useMemo(
    () => (vue === 'options' && statut === 'EN_ATTENTE' ? data.filter((r) => (Number(r.disponible_maintenant) || 0) >= Number(r.qte || 1)).length : 0),
    [data, vue, statut],
  );

  if (!allowed) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Commerce" title="Options / pré-commandes" />
        <NdoeError title="Accès réservé" text="Ce module est réservé à l'administration et au secrétariat." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Commerce"
        title="Options / pré-commandes"
        subtitle="Demandes des clientes sur du stock absent ou à venir. À chaque réception correspondante, vous êtes notifié pour honorer."
        actions={<NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Poser une option</NdoeActionButton>}
      />

      <div className="pc-toolbar">
        <Segmented value={vue} onChange={setVue} options={[
          { value: 'options', label: 'Options' },
          { value: 'demande', label: 'Demande non satisfaite' },
        ]} />
        {vue === 'options' && (
          <Segmented value={statut} onChange={setStatut} options={[
            { value: 'EN_ATTENTE', label: `En attente${enAttenteCount ? ` · ${enAttenteCount} arrivée(s)` : ''}` },
            { value: 'HONOREE', label: 'Honorées' },
            { value: 'ANNULEE', label: 'Annulées' },
            { value: 'toutes', label: 'Toutes' },
          ]} />
        )}
        <Input allowClear className="pc-search" prefix={<SearchOutlined />} placeholder="Cliente, modèle, note…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {error ? <NdoeError title="Erreur" text={error} onRetry={load} /> : loading ? <NdoeLoading rows={4} /> : (
        <div className="ndoe-card pc-block">
          {vue === 'demande' ? (
            <>
              <div className="ndoe-section__title">Ce qui manque au stock — guide d’achat</div>
              <NdoeResponsiveTable
                data={demande}
                columns={demandeCols}
                rowKey={(r) => `${r.id_produit}-${r.id_taille}-${r.id_couleur}`}
                pagination={{ pageSize: 12 }}
                scroll={{ x: 'max-content' }}
                renderCard={renderDemandeCard}
                empty={{ icon: '📋', title: 'Aucune demande en attente', text: 'Les options posées par les clientes s’agrègent ici par modèle et pointure.' }}
              />
            </>
          ) : (
            <NdoeResponsiveTable
              data={data}
              columns={optionCols}
              rowKey="id_precommande"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              renderCard={renderOptionCard}
              empty={{ icon: '🕗', title: 'Aucune option', text: 'Posez une option pour une cliente avec le bouton en haut de page.' }}
            />
          )}
        </div>
      )}

      {/* --- Poser une option --- */}
      <Modal open={createOpen} onCancel={() => setCreateOpen(false)} footer={null} width={620} title="Poser une option" centered>
        {/* UNE PANNE SE DIT. Sans ce bandeau, les menus déroulants restaient
            vides sans explication : on cherchait la cliente ou la pointure
            qu'on ne trouvait pas, au lieu de savoir qu'il fallait réessayer. */}
        {erreurRef && (
          <NdoeError
            title="Listes indisponibles"
            text={erreurRef}
            onRetry={() => { setErreurRef(null); setEssaiRef((n) => n + 1); }}
          />
        )}
        <div className="ndoe-form-grid">
          <div className="form-controle ndoe-form-grid--full">
            <label>Cliente <span style={{ color: 'red' }}>*</span></label>
            <Select
              showSearch optionFilterProp="label" placeholder="Sélectionnez la cliente" style={{ width: '100%' }}
              value={form.id_client} onChange={(v) => setForm((f) => ({ ...f, id_client: v }))}
              options={clients.map((c) => ({ value: c.id, label: c.nom }))}
            />
          </div>
          <div className="form-controle ndoe-form-grid--full">
            <label>Modèle</label>
            {form.modele ? (
              <div className="pc-modele-pick">
                <NdoeImage src={form.modele.img} alt={form.modele.nom_produit || 'Modèle'} size="md" />
                <div className="pc-modele-txt">
                  <b>{form.modele.nom_produit}</b>
                  <small>{form.modele.marque || '—'}</small>
                </div>
                <NdoeActionButton size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>Changer</NdoeActionButton>
              </div>
            ) : (
              <NdoeActionButton variant="ink" onClick={() => setPickerOpen(true)}>Choisir un modèle du catalogue…</NdoeActionButton>
            )}
            <small style={{ color: 'var(--ndoe-muted)' }}>Ou laissez vide et décrivez le modèle souhaité en note (modèle non catalogué).</small>
          </div>
          <div className="form-controle">
            <label>Pointure {form.modele && <span style={{ color: 'red' }}>*</span>}</label>
            <Select
              showSearch optionFilterProp="label" allowClear placeholder="Pointure demandée" style={{ width: '100%' }}
              value={form.id_taille} onChange={(v) => setForm((f) => ({ ...f, id_taille: v }))}
              // LA POINTURE EST ENREGISTRÉE ICI — c'est le cas grave du 10/08/2026.
              // 48 pointures cohabitent sur 4 systèmes, dont 19 au MÊME libellé :
              // « P35 » existe en européen ET en CLA. On nomme donc le système
              // dès qu'un libellé est ambigu, sinon la cliente attend une paire
              // qu'on ne lui commandera jamais.
              //
              // Les pointures qui SERVENT réellement passent devant (la boutique
              // travaille en européen : 1562 variantes sur 1563).
              options={optionsPointures}
            />
          </div>
          <div className="form-controle">
            <label>Couleur</label>
            <Select
              showSearch optionFilterProp="label" allowClear placeholder="Optionnel" style={{ width: '100%' }}
              value={form.id_couleur} onChange={(v) => setForm((f) => ({ ...f, id_couleur: v }))}
              options={couleurs}
            />
          </div>
          <div className="form-controle">
            <label>Quantité</label>
            <Input type="number" min={1} value={form.qte} onChange={(e) => setForm((f) => ({ ...f, qte: e.target.value }))} />
          </div>
          <div className="form-controle ndoe-form-grid--full">
            <label>Note (style souhaité, précision, modèle non catalogué…)</label>
            <Input.TextArea rows={2} maxLength={255} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          </div>
        </div>
        <div className="pc-modal-actions">
          <NdoeActionButton variant="secondary" onClick={() => setCreateOpen(false)}>Annuler</NdoeActionButton>
          <NdoeActionButton variant="primary" onClick={submitCreate} loading={busy}>Enregistrer l’option</NdoeActionButton>
        </div>
      </Modal>
      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refLists} onSelect={onPickModele} />

      {/* --- Honorer --- */}
      <Modal open={!!honor} onCancel={() => setHonor(null)} footer={null} width={480} title="Honorer l’option" centered>
        {honor && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>
              {modeleTxt(honor.pc)} × {honor.pc.qte} — {honor.pc.nom_client}. La paire sera
              <b> réservée pour la cliente</b> (elle apparaîtra dans les Réservations). Refusé si le
              disponible ne suffit plus.
            </p>
            <div className="form-controle">
              <label>Commentaire</label>
              <Input.TextArea rows={2} maxLength={255} value={honor.commentaire}
                onChange={(e) => setHonor((h) => ({ ...h, commentaire: e.target.value }))}
                placeholder="Ex. cliente prévenue par WhatsApp, passe demain" />
            </div>
            <div className="pc-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setHonor(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="primary" onClick={doHonor} loading={busy}>Honorer et réserver</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      {/* --- Annuler --- */}
      <Modal open={!!cancel} onCancel={() => setCancel(null)} footer={null} width={460} title="Annuler l’option" centered>
        {cancel && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>{modeleTxt(cancel.pc)} — {cancel.pc.nom_client}.</p>
            <div className="form-controle">
              <label>Motif</label>
              <Input.TextArea rows={2} maxLength={200} value={cancel.motif}
                onChange={(e) => setCancel((c) => ({ ...c, motif: e.target.value }))}
                placeholder="Ex. la cliente a renoncé" />
            </div>
            <div className="pc-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setCancel(null)}>Fermer</NdoeActionButton>
              <NdoeActionButton variant="danger" onClick={doCancel} loading={busy}>Annuler l’option</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      <AcomptePanel
        cible={acompte}
        open={!!acompte}
        onClose={() => setAcompte(null)}
        onChange={load}
      />
    </NdoePage>
  );
}
